
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Business {
  id: string;
  name: string;
  monthlyFee: number;
}

interface Budget {
  id: string;
  businessName: string;
  budget: number;
}

interface NextChargeSectionProps {
  activeBusinesses: Business[];
  pausedBusinesses: Business[];
  managedBudgets: Budget[];
  availableCredits: number;
  promotionalDiscount: number;
  totalCharge: number;
  formatCurrency: (amount: number) => string;
}

const NextChargeSection = ({
  activeBusinesses,
  pausedBusinesses,
  managedBudgets,
  availableCredits,
  promotionalDiscount,
  totalCharge,
  formatCurrency
}: NextChargeSectionProps) => {
  const activeBusinessTotal = activeBusinesses.reduce((sum, business) => sum + business.monthlyFee, 0);
  const pausedBusinessTotal = pausedBusinesses.reduce((sum, business) => sum + business.monthlyFee, 0);
  const budgetsTotal = managedBudgets.reduce((sum, budget) => sum + budget.budget, 0);
  
  return <Card className="border border-gray-100 rounded-lg shadow-sm overflow-hidden h-full">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
        <h3 className="text-base font-medium text-gray-800">Next Charge</h3>
      </div>
      <CardContent className="p-6">
        {totalCharge === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <p>No Upcoming Charges</p>
          </div>
        ) : (
          <div className="space-y-1">
            {activeBusinesses.length > 0 && (
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full flex justify-between text-sm cursor-pointer hover:bg-gray-50 py-1 rounded mx-0 px-0">
                    <span>
                      <span className="underline font-medium">Active Businesses</span>
                      {` (${activeBusinesses.length} x $${activeBusinesses[0]?.monthlyFee})`}
                    </span>
                    <span className="font-medium w-20 text-right">{formatCurrency(activeBusinessTotal)}</span>
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Active Businesses</DialogTitle>
                    <DialogDescription>
                      The following active businesses are included in your subscription.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business Name</TableHead>
                          <TableHead className="text-right">Fee</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeBusinesses.map(business => <TableRow key={business.id}>
                            <TableCell>{business.name}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(business.monthlyFee)}
                            </TableCell>
                          </TableRow>)}
                        <TableRow>
                          <TableCell className="font-semibold">Total</TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(activeBusinessTotal)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {pausedBusinesses.length > 0 && (
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full flex justify-between text-sm cursor-pointer hover:bg-gray-50 py-1 rounded px-0 mx-0">
                    <span>
                      <span className="underline font-medium">Paused Businesses</span>
                      {` (${pausedBusinesses.length} x $${pausedBusinesses[0]?.monthlyFee})`}
                    </span>
                    <span className="font-medium w-20 text-right">{formatCurrency(pausedBusinessTotal)}</span>
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Paused Businesses</DialogTitle>
                    <DialogDescription>
                      The following paused businesses are included in your subscription at a reduced rate.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business Name</TableHead>
                          <TableHead className="text-right">Fee</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pausedBusinesses.map(business => <TableRow key={business.id}>
                            <TableCell>{business.name}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(business.monthlyFee)}
                            </TableCell>
                          </TableRow>)}
                        <TableRow>
                          <TableCell className="font-semibold">Total</TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(pausedBusinessTotal)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {managedBudgets.length > 0 && (
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full flex justify-between text-sm cursor-pointer hover:bg-gray-50 py-1 rounded px-0 mx-0">
                    <span>
                      <span className="underline font-medium">Managed Budgets</span>
                      {` (${managedBudgets.length})`}
                    </span>
                    <span className="font-medium w-20 text-right">{formatCurrency(budgetsTotal)}</span>
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Managed Budgets</DialogTitle>
                    <DialogDescription>
                      The following managed budgets are included in your next charge.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {managedBudgets.map(budget => <TableRow key={budget.id}>
                            <TableCell>{budget.businessName}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(budget.budget)}
                            </TableCell>
                          </TableRow>)}
                        <TableRow>
                          <TableCell className="font-semibold">Total</TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(budgetsTotal)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {promotionalDiscount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Promo Codes (1)</span>
                <span className="font-medium w-20 text-right">-{formatCurrency(promotionalDiscount)}</span>
              </div>
            )}
            
            {availableCredits > 0 && (
              <div className="flex justify-between text-sm text-green-600 pb-3">
                <span>Available Credits</span>
                <span className="font-medium w-20 text-right">-{formatCurrency(availableCredits)}</span>
              </div>
            )}
            
            <Separator className="my-2" />
            <div className="flex justify-between text-sm pt-2 font-semibold">
              <span>Aug 27th, 2025</span>
              <span className="w-20 text-right">{formatCurrency(totalCharge)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>;
};

export default NextChargeSection;
